import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ChatShell,
  ChangeReviewPanel,
  Bubble,
  ReviewContinueAction,
  ReviewRequirementCue,
  ScenarioPanel,
} from "../shared/components";
import { FinalReview } from "../shared/FinalReview";
import { RecallQuestionnaire } from "../shared/RecallQuestionnaire";
import {
  EDIT_VALUE,
  FLAG_FOR_FINAL_REVIEW,
  NO_MISMATCH_ACTION,
  NOT_REVIEWED,
  conditionDefinition,
  isMismatchAction,
  isSubstantiveReviewStatus,
} from "../shared/conditionContract";
import { FORM_SCHEMA } from "../shared/formSchema";
import { expectedFieldIds, orderReviewEditsBySchema } from "../shared/fieldOrder";
import { SCENARIO } from "../shared/mockData";
import { currentTurnReview } from "../shared/currentTurnReview";
import { useStudyTelemetry } from "../shared/telemetry";
import { createClientTurnId, updateRecord, useStudySession } from "../shared/useStudySession";

export const SUBMIT_TELEMETRY_TIMEOUT_MS = 1500;

async function withTimeout(promise, timeoutMs) {
  let timeoutId;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => {
        timeoutId = setTimeout(
          () => reject(new Error("submit telemetry flush timed out")),
          timeoutMs
        );
      }),
    ]);
  } finally {
    clearTimeout(timeoutId);
  }
}

function isFilled(value) {
  if (value === null || value === undefined) return false;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    return normalized !== "" && normalized !== "open" && normalized !== "<open>";
  }
  if (Array.isArray(value)) return value.length > 0;
  if (typeof value === "object") return Object.keys(value).length > 0;
  return true;
}

function valueChanged(original, value) {
  return String(original ?? "") !== String(value ?? "");
}

function eventId(prefix, fieldId) {
  const suffix = typeof crypto !== "undefined" && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${fieldId}:${suffix}`;
}

function latestReviewStatuses(events) {
  const latest = {};
  for (const event of Object.values(events || {})) {
    if (event?.proposal_id && isSubstantiveReviewStatus(event.new_status)) {
      latest[event.proposal_id] = event.new_status;
    }
  }
  return latest;
}

function latestReviewStatusEventIds(events) {
  const latest = {};
  for (const event of Object.values(events || {})) {
    if (event?.proposal_id && isSubstantiveReviewStatus(event.new_status)) {
      latest[event.proposal_id] = event.event_id;
    }
  }
  return latest;
}

function latestMismatchActionStates(events) {
  const latest = {};
  for (const event of Object.values(events || {})) {
    if (event?.proposal_id && isMismatchAction(event.new_action)) {
      latest[event.proposal_id] = {
        action: event.new_action,
        reviewStatusEventId: event.review_status_event_id,
      };
    }
  }
  return latest;
}

function applyEditEvents(record, events) {
  let next = { ...(record || {}) };
  for (const edit of Object.values(events || {})) {
    if (edit?.field_id) next = updateRecord(next, edit.field_id, edit.value);
  }
  return next;
}

function recordHasRequiredFields(record, schema) {
  const required = expectedFieldIds(schema, record);
  return required.length > 0 && required.every((id) => isFilled(record?.[id]));
}

function notifyQualtricsCompletion({
  room,
  condition,
  eventType,
  severityRatingItems,
  completionToken,
}) {
  if (typeof window === "undefined" || window.parent === window) return;
  try {
    window.parent.postMessage(
      {
        type: "studyComplete",
        status: "success",
        room,
        condition,
        event_type: eventType,
        timestamp: new Date().toISOString(),
        severity_rating_items: Array.isArray(severityRatingItems)
          ? severityRatingItems
          : [],
        completion_token: completionToken || null,
      },
      "*"
    );
  } catch (err) {
    // Completion is already persisted; never block the in-app submitted state on parent messaging.
  }
}

export default function LiveStudy({ condition, room, cfg, studySession }) {
  const live = useStudySession({ room, session: studySession, condition });
  const telemetry = useStudyTelemetry(live.postTelemetry, {
    studyProtocolVersion: cfg?.study_protocol_version || "unversioned",
    studyBuildId: cfg?.study_build_id || null,
  });
  const [submitted, setSubmitted] = useState(Boolean(cfg?.form_submitted));
  const [recallRequired, setRecallRequired] = useState(Boolean(cfg?.recall_required));
  const [reviewStatusEvents, setReviewStatusEvents] = useState({});
  const [mismatchActionEvents, setMismatchActionEvents] = useState({});
  const [drafts, setDrafts] = useState({});
  const [finalRecord, setFinalRecord] = useState(null);
  const [finalReviewBaseline, setFinalReviewBaseline] = useState(null);
  const [savingDecisionIds, setSavingDecisionIds] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [openingFinalReview, setOpeningFinalReview] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const finalEditEventsRef = useRef({});

  const code = (cfg?.condition || condition || "INSTANT_OPT").toUpperCase();
  const conditionContract = conditionDefinition(
    code,
    cfg?.config,
    cfg?.verification_card
  );
  const verificationSchedule = conditionContract.verification_schedule;
  const responseRequirement = conditionContract.response_requirement;
  const verificationCard = conditionContract.verification_card;
  const sourcePacket = cfg?.source_packet || SCENARIO;
  const instant = verificationSchedule === "INSTANT";
  const responseRequired = responseRequirement === "REQUIRED";
  const edits = useMemo(
    () => live.items.filter((it) => it.kind === "edit"),
    [live.items]
  );
  const instantTurn = useMemo(
    () => currentTurnReview(live.items),
    [live.items]
  );
  const visibleReviewEdits = useMemo(
    () => orderReviewEditsBySchema(
      instant ? instantTurn.edits : edits,
      FORM_SCHEMA,
      live.record
    ),
    [edits, instant, instantTurn.edits, live.record]
  );
  const messageItems = useMemo(
    () => live.items.filter((it) => it.kind === "msg"),
    [live.items]
  );
  const reportProgress = useMemo(() => {
    for (let index = messageItems.length - 1; index >= 0; index -= 1) {
      const progress = messageItems[index]?.payload?.progress;
      const current = Number(progress?.current);
      const total = Number(progress?.total);
      if (Number.isFinite(current) && Number.isFinite(total) && total > 0) {
        return {
          current: Math.max(0, Math.min(current, total)),
          total,
        };
      }
    }
    return null;
  }, [messageItems]);
  const reportProgressPercent = reportProgress
    ? Math.round((reportProgress.current / reportProgress.total) * 100)
    : 0;
  const reviewStatuses = useMemo(
    () => latestReviewStatuses(reviewStatusEvents),
    [reviewStatusEvents]
  );
  const reviewStatusEventIds = useMemo(
    () => latestReviewStatusEventIds(reviewStatusEvents),
    [reviewStatusEvents]
  );
  const mismatchActionStates = useMemo(
    () => latestMismatchActionStates(mismatchActionEvents),
    [mismatchActionEvents]
  );
  const mismatchActions = useMemo(() => {
    const latest = {};
    for (const [proposalId, state] of Object.entries(mismatchActionStates)) {
      if (
        reviewStatuses[proposalId] === "DOES_NOT_MATCH_SOURCE"
        && state.reviewStatusEventId === reviewStatusEventIds[proposalId]
      ) {
        latest[proposalId] = state.action;
      }
    }
    return latest;
  }, [mismatchActionStates, reviewStatusEventIds, reviewStatuses]);
  const allPendingEdits = useMemo(
    () => edits.filter((it) => !reviewStatuses[it.proposal_id || it.event_id || it.id]),
    [edits, reviewStatuses]
  );
  const pendingEdits = useMemo(
    () => visibleReviewEdits.filter(
      (it) => (
        !reviewStatuses[it.proposal_id || it.event_id || it.id]
        || Boolean(savingDecisionIds[it.id])
      )
    ),
    [reviewStatuses, savingDecisionIds, visibleReviewEdits]
  );
  const pending = useMemo(
    () => pendingEdits.length > 0,
    [pendingEdits]
  );
  const sourceFocusFieldIds = useMemo(() => {
    const blockedForInstantReview = instant && responseRequired && pending;
    if (live.complete || live.agentBusy || live.error || blockedForInstantReview) {
      return [];
    }
    const latestBotMessage = [...messageItems]
      .reverse()
      .find((item) => item.sender === "bot");
    const payloadFocus = latestBotMessage?.payload?.source_focus_field_ids;
    return Array.isArray(payloadFocus) ? payloadFocus.filter(Boolean) : [];
  }, [
    instant,
    responseRequired,
    pending,
    live.complete,
    live.agentBusy,
    live.error,
    messageItems,
  ]);
  const verificationStageComplete = Boolean(live.uiState?.verification_stage);
  const showFieldReview = Boolean(
    !submitted
    && visibleReviewEdits.length > 0
    && !verificationStageComplete
    && (instant || live.complete)
  );
  const inVerificationStage = Boolean(live.complete && !verificationStageComplete);
  const showSharedFinalReview = verificationStageComplete && !submitted;
  const reviewMode = (inVerificationStage || showSharedFinalReview) && !submitted;
  const showVerificationContinueFooter = inVerificationStage;
  const inlineReviewBeforeMessageId = instant && showFieldReview
    ? instantTurn.beforeMessageId
    : null;

  useEffect(() => {
    setReviewStatusEvents((current) => ({
      ...current,
      ...(live.uiState?.review_status_events || {}),
    }));
  }, [live.uiState?.review_status_events]);

  useEffect(() => {
    setMismatchActionEvents((current) => ({
      ...current,
      ...(live.uiState?.mismatch_action_events || {}),
    }));
  }, [live.uiState?.mismatch_action_events]);

  useEffect(() => {
    if (live.uiState?.submitted) setSubmitted(true);
  }, [live.uiState?.submitted]);

  useEffect(() => {
    if (cfg?.recall_required) setRecallRequired(true);
  }, [cfg?.recall_required]);

  useEffect(() => {
    finalEditEventsRef.current = {
      ...(live.uiState?.final_edits || {}),
      ...finalEditEventsRef.current,
    };
  }, [live.uiState?.final_edits]);

  useEffect(() => {
    if (showSharedFinalReview && finalRecord === null) {
      const snapshot = {
        ...(live.uiState?.verification_stage?.record_snapshot || live.record),
      };
      setFinalReviewBaseline(snapshot);
      setFinalRecord(snapshot);
    }
  }, [finalRecord, live.record, live.uiState?.verification_stage, showSharedFinalReview]);

  const phase = submitted
    ? recallRequired ? "recall" : "submitted"
    : showSharedFinalReview
      ? "final_review"
      : showFieldReview
        ? "field_review"
      : live.connected
        ? "conversation"
        : "loading";

  const emitTelemetry = useCallback((event) => {
    telemetry.send({ phase, ...event }).catch(() => {});
  }, [phase, telemetry]);

  useEffect(() => {
    if (live.connected && !submitted) {
      telemetry.startTask({ trigger: "study_connected", phase: "conversation" }).catch(() => {});
    }
  }, [live.connected, submitted, telemetry]);

  const handleRecallComplete = useCallback((completed = {}) => {
    notifyQualtricsCompletion({
      room,
      condition: code,
      eventType: "recall_complete",
      severityRatingItems: [],
      completionToken: completed.completion_token,
    });
  }, [code, room]);

  const submit = async (eventType = "complete", extra = {}) => {
    if (!verificationStageComplete || submitting) return;
    const recordToSubmit = finalRecord || live.record;
    if (!recordHasRequiredFields(recordToSubmit, FORM_SCHEMA)) {
      setSubmitError("Every report field must contain a value before submission.");
      telemetry.send({
        event_type: "submit_blocked_incomplete",
        phase: "final_review",
        surface: "final_review",
      }).catch(() => {});
      return;
    }

    setSubmitting(true);
    setSubmitError("");
    try {
      let telemetryCheckpoint = null;
      let telemetryFlushStatus = "checkpoint_unavailable";
      const safeTelemetryCheckpoint = () => {
        try {
          return telemetry.checkpoint ? telemetry.checkpoint() : null;
        } catch (checkpointError) {
          return null;
        }
      };
      try {
        // submit_attempt bypasses the observational queue. A geometry or
        // visibility backlog must never delay the authoritative submission.
        await withTimeout(telemetry.send({
          event_type: "submit_attempt",
          phase: "final_review",
          surface: "final_review",
          ...extra,
        }), SUBMIT_TELEMETRY_TIMEOUT_MS);
        telemetryCheckpoint = safeTelemetryCheckpoint();
        if (telemetryCheckpoint) {
          telemetryFlushStatus = telemetryCheckpoint.pending_event_count > 0
            ? "pending"
            : "flushed";
        }
        if (telemetryCheckpoint?.pending_event_count > 0 && telemetry.flushPending) {
          telemetry.flushPending().catch(() => {});
        }
      } catch (telemetryError) {
        telemetryCheckpoint = safeTelemetryCheckpoint();
        telemetryFlushStatus = "flush_failed";
      }
      const baseline = finalReviewBaseline || live.record;
      const finalEdits = { ...finalEditEventsRef.current };
      const expectedAfterSavedEdits = applyEditEvents(baseline, finalEdits);
      for (const fieldId of expectedFieldIds(FORM_SCHEMA, recordToSubmit)) {
        if (!valueChanged(expectedAfterSavedEdits[fieldId], recordToSubmit[fieldId])) continue;
        const id = eventId("final-submit", fieldId);
        finalEdits[id] = {
          event_id: id,
          field_id: fieldId,
          original_value: expectedAfterSavedEdits[fieldId],
          value: recordToSubmit[fieldId],
          client_ts: new Date().toISOString(),
          source: "submit_flush",
        };
        expectedAfterSavedEdits[fieldId] = recordToSubmit[fieldId];
      }
      finalEditEventsRef.current = finalEdits;
      const result = await live.submitRecord({
        record: recordToSubmit,
        finalEdits,
        telemetryCheckpoint,
        telemetryFlushStatus,
      });
      setSubmitted(true);
      setRecallRequired(Boolean(result.recall_required));
      live.setComplete(true);
      if (result.recall_required) {
        telemetry.send({
          event_type: "submission_locked",
          phase: "recall",
          surface: "final_review",
          submission_id: result.submission_id,
          idempotent: result.idempotent,
        }).catch(() => {});
      } else {
        telemetry.send({
          event_type: "study_complete",
          phase: "submitted",
          surface: "final_review",
          submission_id: result.submission_id,
          idempotent: result.idempotent,
        }).catch(() => {});
        notifyQualtricsCompletion({
          room,
          condition: code,
          eventType,
          severityRatingItems: result.severity_rating_items,
          completionToken: result.completion_token,
        });
      }
    } catch (error) {
      setSubmitError(error?.message || "The report could not be submitted. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const changeReviewStatus = async (edit, newStatus, { surface = "field_review" } = {}) => {
    if (savingDecisionIds[edit.id]) return false;
    if (!isSubstantiveReviewStatus(newStatus)) return false;
    const proposalId = edit.proposal_id || edit.event_id || edit.id;
    const oldStatus = reviewStatuses[proposalId] || NOT_REVIEWED;
    if (newStatus === oldStatus) return false;
    const statusEventId = eventId("review-status", proposalId);
    const statusEvent = {
      event_id: statusEventId,
      event_name: "review_status_changed",
      proposal_id: proposalId,
      proposal_version: edit.proposal_version || 1,
      record_version: edit.record_version,
      current_record_version: (edit.current_record_version || edit.record_version) + 1,
      field_id: edit.field_id,
      ai_proposed_value: edit.ai_proposed_value,
      source_evidence_id: edit.source_evidence_id,
      source_evidence_version: edit.source_evidence_version,
      old_status: oldStatus,
      new_status: newStatus,
      client_timestamp: new Date().toISOString(),
    };
    setSavingDecisionIds((current) => ({ ...current, [edit.id]: true }));
    setReviewStatusEvents((current) => ({ ...current, [statusEventId]: statusEvent }));
    setSubmitError("");
    try {
      const patch = { review_status_events: { [statusEventId]: statusEvent } };
      const saved = await live.saveUiState(patch);
      setReviewStatusEvents((current) => ({
        ...current,
        ...(saved.review_status_events || {}),
      }));
      telemetry.send({
        ...statusEvent,
        event_type: "review_status_changed",
        phase: "field_review",
        surface,
        response_requirement: responseRequirement,
        schedule: verificationSchedule,
      }).catch(() => {});
      return true;
    } catch (error) {
      setReviewStatusEvents((current) => {
        const next = { ...current };
        delete next[statusEventId];
        return next;
      });
      setSubmitError("The review status could not be saved. Please try again.");
      return false;
    } finally {
      setSavingDecisionIds((current) => {
        const next = { ...current };
        delete next[edit.id];
        return next;
      });
    }
  };

  const correctField = async (edit, value, { surface = "field_review" } = {}) => {
    if (savingDecisionIds[edit.id]) return false;
    const currentValue = edit.saved_value !== undefined
      ? edit.saved_value
      : edit.original_value !== undefined
        ? edit.original_value
        : edit.value;
    if (!isFilled(value) || !valueChanged(currentValue, value)) return false;
    const proposalEventId = edit.proposal_id || edit.event_id || edit.id;
    const correctionEventId = eventId("field-correction", edit.id);
    const currentRecordVersion = edit.current_record_version ?? edit.record_version;
    const nextRecordVersion = currentRecordVersion + 1;
    const correction = {
      event_id: correctionEventId,
      event_name: "field_edit_committed",
      proposal_id: proposalEventId,
      proposal_version: edit.proposal_version || 1,
      record_version: edit.record_version,
      current_record_version: nextRecordVersion,
      field_id: edit.field_id,
      ai_proposed_value: edit.ai_proposed_value,
      source_evidence_id: edit.source_evidence_id,
      source_evidence_version: edit.source_evidence_version,
      original_value: currentValue,
      value,
      client_timestamp: new Date().toISOString(),
    };
    const status = reviewStatuses[proposalEventId] || NOT_REVIEWED;
    const currentMismatchAction = mismatchActions[proposalEventId] || NO_MISMATCH_ACTION;
    let mismatchActionEvent = null;
    if (status === "DOES_NOT_MATCH_SOURCE" && currentMismatchAction !== EDIT_VALUE) {
      const reviewStatusEventId = reviewStatusEventIds[proposalEventId];
      if (!reviewStatusEventId) {
        setSubmitError("The report value cannot be edited until the review status is saved.");
        return false;
      }
      const mismatchActionEventId = eventId("mismatch-action", proposalEventId);
      mismatchActionEvent = {
        event_id: mismatchActionEventId,
        event_name: "mismatch_action_changed",
        proposal_id: proposalEventId,
        proposal_version: edit.proposal_version || 1,
        record_version: edit.record_version,
        field_id: edit.field_id,
        ai_proposed_value: edit.ai_proposed_value,
        source_evidence_id: edit.source_evidence_id,
        source_evidence_version: edit.source_evidence_version,
        review_status_event_id: reviewStatusEventId,
        correction_event_id: correctionEventId,
        old_action: currentMismatchAction,
        new_action: EDIT_VALUE,
        client_timestamp: new Date().toISOString(),
      };
    }
    setSavingDecisionIds((current) => ({ ...current, [edit.id]: true }));
    setSubmitError("");
    try {
      const patch = {
        edits: { [correctionEventId]: correction },
        ...(mismatchActionEvent
          ? { mismatch_action_events: { [mismatchActionEvent.event_id]: mismatchActionEvent } }
          : {}),
      };
      const saved = await live.saveUiState(patch);
      if (mismatchActionEvent) {
        setMismatchActionEvents((current) => ({
          ...current,
          ...(saved.mismatch_action_events || {}),
        }));
      }
      live.setRecord((record) => updateRecord(record, edit.field_id, value));
      setDrafts((current) => {
        const next = { ...current };
        delete next[edit.id];
        return next;
      });
      telemetry.send({
        event_type: "field_edit_committed",
        phase: "field_review",
        surface,
        field_id: edit.field_id,
        edit_id: proposalEventId,
        correction_event_id: correctionEventId,
        record_version: edit.record_version,
        current_record_version: nextRecordVersion,
        original_value: currentValue,
        value,
        response_requirement: responseRequirement,
        schedule: verificationSchedule,
      }).catch(() => {});
      if (mismatchActionEvent) {
        telemetry.send({
          ...mismatchActionEvent,
          event_type: "mismatch_action_changed",
          phase: "field_review",
          surface,
          response_requirement: responseRequirement,
          schedule: verificationSchedule,
        }).catch(() => {});
      }
      return true;
    } catch (error) {
      setSubmitError("The report value edit could not be saved. Please try again.");
      return false;
    } finally {
      setSavingDecisionIds((current) => {
        const next = { ...current };
        delete next[edit.id];
        return next;
      });
    }
  };

  const flagForFinalReview = async (edit, action, { surface = "field_review" } = {}) => {
    if (savingDecisionIds[edit.id]) return false;
    if (action !== FLAG_FOR_FINAL_REVIEW) return false;
    const proposalId = edit.proposal_id || edit.event_id || edit.id;
    if (reviewStatuses[proposalId] !== "DOES_NOT_MATCH_SOURCE") return false;
    if (mismatchActions[proposalId] === FLAG_FOR_FINAL_REVIEW) return true;
    const reviewStatusEventId = reviewStatusEventIds[proposalId];
    if (!reviewStatusEventId) return false;
    const mismatchActionEventId = eventId("mismatch-action", proposalId);
    const mismatchActionEvent = {
      event_id: mismatchActionEventId,
      event_name: "mismatch_action_changed",
      proposal_id: proposalId,
      proposal_version: edit.proposal_version || 1,
      record_version: edit.record_version,
      field_id: edit.field_id,
      ai_proposed_value: edit.ai_proposed_value,
      source_evidence_id: edit.source_evidence_id,
      source_evidence_version: edit.source_evidence_version,
      review_status_event_id: reviewStatusEventId,
      correction_event_id: null,
      old_action: mismatchActions[proposalId] || NO_MISMATCH_ACTION,
      new_action: FLAG_FOR_FINAL_REVIEW,
      client_timestamp: new Date().toISOString(),
    };
    setSavingDecisionIds((current) => ({ ...current, [edit.id]: true }));
    setSubmitError("");
    try {
      const saved = await live.saveUiState({
        mismatch_action_events: { [mismatchActionEventId]: mismatchActionEvent },
      });
      setMismatchActionEvents((current) => ({
        ...current,
        ...(saved.mismatch_action_events || {}),
      }));
      telemetry.send({
        ...mismatchActionEvent,
        event_type: "mismatch_action_changed",
        phase: "field_review",
        surface,
        response_requirement: responseRequirement,
        schedule: verificationSchedule,
      }).catch(() => {});
      return true;
    } catch (error) {
      setSubmitError("The final-review flag could not be saved. Please try again.");
      return false;
    } finally {
      setSavingDecisionIds((current) => {
        const next = { ...current };
        delete next[edit.id];
        return next;
      });
    }
  };

  const recordBlockedProgressAttempt = useCallback((surface) => {
    if (!responseRequired || !pending) return;
    setSubmitError(verificationCard.required_validation_copy);
    telemetry.send({
      event_type: "progress_attempt_blocked",
      phase: "field_review",
      surface,
      block_reason: "NOT_REVIEWED",
      pending_proposal_count: pendingEdits.length,
      schedule: verificationSchedule,
      response_requirement: responseRequirement,
    }).catch(() => {});
  }, [
    pending,
    pendingEdits.length,
    responseRequired,
    responseRequirement,
    telemetry,
    verificationCard.required_validation_copy,
    verificationSchedule,
  ]);

  const completeVerificationStage = async () => {
    if (!live.complete || openingFinalReview || verificationStageComplete) return;
    if (responseRequired && pending) {
      recordBlockedProgressAttempt(`${verificationSchedule.toLowerCase()}_review`);
      return;
    }
    const stageEventId = eventId("verification-stage", code);
    const clientTimestamp = new Date().toISOString();
    setOpeningFinalReview(true);
    setSubmitError("");
    try {
      await live.saveUiState({
        verification_stage_completed: {
          event_id: stageEventId,
          client_timestamp: clientTimestamp,
        },
      });
      await telemetry.send({
        event_id: stageEventId,
        event_type: "verification_stage_completed",
        phase: "field_review",
        surface: `${verificationSchedule.toLowerCase()}_review`,
        client_timestamp: clientTimestamp,
        reviewed_proposal_count: edits.length - allPendingEdits.length,
        proposal_count: edits.length,
        schedule: verificationSchedule,
        response_requirement: responseRequirement,
      });
    } catch (error) {
      setSubmitError(error?.message || "Final Review could not be opened. Please try again.");
    } finally {
      setOpeningFinalReview(false);
    }
  };

  const saveFinalEdit = useCallback((change) => {
    if (!change?.field_id) return;
    const id = eventId("final-edit", change.field_id);
    const editEvent = {
      event_id: id,
      event_name: "final_field_edit_committed",
      field_id: change.field_id,
      original_value: change.original_value,
      value: change.value,
      client_timestamp: new Date().toISOString(),
      source: "field_blur",
    };
    finalEditEventsRef.current = {
      ...finalEditEventsRef.current,
      [id]: editEvent,
    };
    live.saveUiState({
      final_edits: {
        [id]: editEvent,
      },
    }).catch(() => {});
  }, [live]);

  const finalReviewAnnotations = useMemo(() => {
    const correctionByProposal = {};
    for (const correction of Object.values(live.uiState?.edits || {})) {
      if (correction?.proposal_id) correctionByProposal[correction.proposal_id] = correction;
    }
    const annotations = {};
    for (const edit of edits) {
      const proposalId = edit.proposal_id || edit.event_id || edit.id;
      const status = reviewStatuses[proposalId] || NOT_REVIEWED;
      if (status === "DOES_NOT_MATCH_SOURCE") {
        if (correctionByProposal[proposalId]) {
          annotations[edit.field_id] = {
            tone: "edited",
            lines: verificationCard.mismatch_edited_copy,
          };
        } else if (mismatchActions[proposalId] === FLAG_FOR_FINAL_REVIEW) {
          annotations[edit.field_id] = {
            tone: "flagged",
            text: verificationCard.mismatch_flagged_copy,
          };
        } else {
          annotations[edit.field_id] = {
            tone: "flagged",
            text: "Original proposal marked as not matching",
          };
        }
      } else if (status === "CANNOT_DETERMINE") {
        annotations[edit.field_id] = {
          tone: "unclear",
          text: verificationCard.unclear_copy,
        };
      }
    }
    return annotations;
  }, [edits, live.uiState?.edits, mismatchActions, reviewStatuses, verificationCard]);

  const renderFieldReviewPanel = (key = "field-review") => (
    <ChangeReviewPanel
      key={key}
      schedule={verificationSchedule}
      responseRequirement={responseRequirement}
      verificationCard={verificationCard}
      sourcePacket={sourcePacket}
      edits={visibleReviewEdits}
      currentRecord={live.record}
      currentRecordVersions={live.recordVersions}
      corrections={live.uiState?.edits || {}}
      reviewStatuses={reviewStatuses}
      mismatchActions={mismatchActions}
      drafts={drafts}
      submitted={submitted}
      showContinue={live.complete && !showVerificationContinueFooter}
      continueDisabled={responseRequired && pending}
      continuing={openingFinalReview}
      validationMessage={submitError}
      onDraftChange={(id, value) => setDrafts((s) => ({ ...s, [id]: value }))}
      onStatusChange={(edit, status) => changeReviewStatus(edit, status, {
        surface: `${verificationSchedule.toLowerCase()}_review`,
      })}
      onMismatchAction={(edit, action) => flagForFinalReview(edit, action, {
        surface: `${verificationSchedule.toLowerCase()}_review`,
      })}
      onCorrect={(edit, value) => correctField(edit, value, {
        surface: `${verificationSchedule.toLowerCase()}_review`,
      })}
      onContinue={completeVerificationStage}
      onBlockedAttempt={() => recordBlockedProgressAttempt(
        `${verificationSchedule.toLowerCase()}_review`
      )}
      savingStatusIds={savingDecisionIds}
      onTelemetry={emitTelemetry}
    />
  );

  const renderInlineReviewPanel = () => {
    if (showFieldReview && inlineReviewBeforeMessageId) {
      return renderFieldReviewPanel("field-review-inline");
    }
    return null;
  };

  if (submitted && recallRequired) {
    return (
      <RecallQuestionnaire
        loadState={live.loadRecallState}
        saveResponse={live.saveRecallResponse}
        completeRecall={live.completeRecall}
        onComplete={handleRecallComplete}
        onTelemetry={emitTelemetry}
      />
    );
  }

  return (
    <div className="study-root">
      <div className="study-topbar">
        <h1>Accident Report</h1>
        <div className="study-topbar-right">
          <span className={`socket-dot ${live.connected ? "on" : ""}`} />
          <span className="socket-label">{live.status}</span>
        </div>
      </div>
      {reportProgress && (
        <div className="study-task-progress">
          <span className="study-task-progress-label">Report progress</span>
          <div
            className="study-task-progress-track"
            role="progressbar"
            aria-label="Report progress"
            aria-valuemin="0"
            aria-valuemax={reportProgress.total}
            aria-valuenow={reportProgress.current}
          >
            <div
              className="study-task-progress-fill"
              style={{ width: `${reportProgressPercent}%` }}
            />
          </div>
          <span className="study-task-progress-count">
            {reportProgress.current} of {reportProgress.total} fields
          </span>
        </div>
      )}
      <div className="study-split scenario">
        <ScenarioPanel
          scenario={sourcePacket}
          onTelemetry={emitTelemetry}
          activeFieldIds={sourceFocusFieldIds}
        />
        <ChatShell
          locked={instant && responseRequired && pending && !live.complete}
          lockedReason={verificationCard.required_validation_copy}
          onLockedAttempt={() => recordBlockedProgressAttempt("chat")}
          disabled={Boolean(live.error) || submitted || live.agentBusy || reviewMode}
          autoScroll={!reviewMode}
          scrollToBottomKey={`${messageItems[messageItems.length - 1]?.id || "empty"}:${live.agentBusy ? "busy" : "idle"}`}
          notice={showFieldReview ? (
            <ReviewRequirementCue
              responseRequirement={responseRequirement}
              progressionCue={conditionContract.progression_cue}
            />
          ) : null}
          footer={showVerificationContinueFooter ? (
            <ReviewContinueAction
              continueDisabled={responseRequired && pending}
              continuing={openingFinalReview}
              validationMessage={submitError}
              requiredValidationCopy={verificationCard.required_validation_copy}
              onContinue={completeVerificationStage}
              onBlockedAttempt={() => recordBlockedProgressAttempt(`${verificationSchedule.toLowerCase()}_review`)}
            />
          ) : null}
          onSend={(text) => {
            const clientTurnId = createClientTurnId();
            emitTelemetry({
              event_type: "conversation_turn_sent",
              surface: "chat",
              client_turn_id: clientTurnId,
              character_count: text.length,
            });
            return live.sendMessage(text, { clientTurnId });
          }}
          placeholder={
            reviewMode
              ? "Review the report before submitting..."
              : live.agentBusy ? "Assistant is thinking..." : "Type your answer..."
          }
        >
          {live.error && (
            <div className="chat-sys error">
              Study connection failed. Please return to the survey and reopen the study frame.
            </div>
          )}

          {!live.error && live.turnError && (
            <div className="chat-sys error">{live.turnError}</div>
          )}

          {!live.error && messageItems.length === 0 && (
            <div className="chat-sys">Connecting to the study assistant...</div>
          )}

          {messageItems.map((it) => (
            <React.Fragment key={it.id}>
              {inlineReviewBeforeMessageId === it.id && renderInlineReviewPanel()}
              <Bubble m={it} onTelemetry={emitTelemetry} />
            </React.Fragment>
          ))}

          {showFieldReview && !inlineReviewBeforeMessageId && renderFieldReviewPanel()}

          {!live.error && live.agentBusy && (
            <div className="chat-sys thinking">Assistant is thinking...</div>
          )}

          {showSharedFinalReview && !submitted && (
            <FinalReview
              record={finalRecord || live.record}
              setRecord={setFinalRecord}
              onSubmit={() => submit("complete")}
              onTelemetry={emitTelemetry}
              onFieldCommit={saveFinalEdit}
              submitting={submitting}
              submitError={submitError}
              fieldAnnotations={finalReviewAnnotations}
            />
          )}

          {submitted && <div className="chat-sys ok">Report submitted ✓</div>}
        </ChatShell>
      </div>
    </div>
  );
}
